import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiUrlHelper } from '../../services/api-url-helper/api-url-helper';
import { ApiService } from '../../services/api.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../environments/environment';
import { StorageKey, StorageService } from '../../services/common/storage.service';
import { ExistingCustomerDialogComponent } from '../existing-customer-dialog/existing-customer-dialog.component';

@Component({
  selector: 'app-social-media-register',
  templateUrl: './social-media-register.component.html',
  styleUrls: ['./social-media-register.component.scss'],
})
export class SocialMediaRegisterComponent implements OnInit {
  uniqueCode: string = '';
  agency: any = null;
  isLoading: boolean = true;
  invalidLink: boolean = false;
  logoFailed: boolean = false;
  currentYear: number = new Date().getFullYear();

  // Step management
  currentStep: 'signup' | 'otp' | 'existing-customer' | 'registration' | 'success' = 'signup';

  // Sign Up form
  signUpForm!: FormGroup;
  isSignUpLoading: boolean = false;

  // Zip code autocomplete (same behaviour as the standard customer signup page)
  filteredZipCodes: string[] = [];

  // OTP form
  otpForm!: FormGroup;
  isOtpLoading: boolean = false;
  otpResendTimer: number = 0;
  otpInterval: any;

  // Existing customer data
  existingCustomerData: any = null;
  isExistingCustomer: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private apiUrl: ApiUrlHelper,
    private apiService: ApiService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.uniqueCode = params['code'] || '';
      if (this.uniqueCode) {
        this.loadAgencyDetails();
      } else {
        this.invalidLink = true;
        this.isLoading = false;
      }
    });

    this.initForms();
  }

  // ---- Zip Code -> County/State (same behaviour as the standard customer signup) ----
  onZipCodeChange(): void {
    const input = this.signUpForm.get('zipCode')?.value;
    if (input && input.length >= 3) {
      this.loadZipCodes(input);
    } else {
      this.filteredZipCodes = [];
      this.signUpForm.patchValue({ county: '', state: '' });
    }
  }

  loadZipCodes(filter: string): void {
    const url = `${this.apiUrl.apiUrl.FirmRegistration.GetZipCodes}?ZipCode=${filter}`;
    this.apiService.doGet(url).subscribe({
      next: (response: any) => {
        if (response?.Success) {
          this.filteredZipCodes = response.Data.map(
            (zipObj: { ZIPCode: string }) => zipObj.ZIPCode
          );
        }
      },
      error: () => {
        this.filteredZipCodes = [];
      },
    });
  }

  onZipCodeSelected(zip: string): void {
    this.signUpForm.get('zipCode')?.setValue(zip);
    this.filteredZipCodes = [];
    this.fetchStateCountyByZip(zip);
  }

  private fetchStateCountyByZip(zipCode: string): void {
    const url = this.apiUrl.apiUrl.FirmRegistration.GetStateCountyCity;
    this.apiService.doPost(url, { ZIPCode: zipCode }).subscribe({
      next: (response: any) => {
        if (response?.Data) {
          this.signUpForm.patchValue({
            county: response.Data.County || '',
            state: response.Data.State || '',
          });
        } else {
          this.signUpForm.patchValue({ county: '', state: '' });
        }
      },
      error: () => {
        this.signUpForm.patchValue({ county: '', state: '' });
      },
    });
  }

  initForms() {
    this.signUpForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      county: [{ value: '', disabled: true }, [Validators.required]],
      state: [{ value: '', disabled: true }, [Validators.required]],
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(6)]],
    });
  }

  // Format the mobile number into US format XXX-XXX-XXXX as the user types.
  formatMobileNumber(): void {
    const control = this.signUpForm.get('mobileNumber');
    if (!control) return;
    const digits = (control.value || '').replace(/\D/g, '').substring(0, 10);
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.substring(0, 3)}-${digits.substring(3)}`;
    }
    control.setValue(formatted, { emitEvent: false });
  }

  loadAgencyDetails() {
    const url = this.apiUrl.apiUrl.socialMediaRegistration.getAgencyByCode.replace('{code}', this.uniqueCode);
    this.apiService.doGet(url).subscribe({
      next: (response) => {
        if (response.Success && response.Data) {
          this.agency = response.Data;
          // Build full logo URL
          if (this.agency.AgencyLogo) {
            const baseUrl = (environment as any).baseUrl || '';
            this.agency.AgencyLogoUrl = baseUrl + this.agency.AgencyLogo.replace(/\\/g, '/');
          }
          this.isLoading = false;
        } else {
          this.invalidLink = true;
          this.isLoading = false;
        }
      },
      error: () => {
        this.invalidLink = true;
        this.isLoading = false;
      },
    });
  }

  // Step 1: Sign Up
  onSignUp() {
    this.signUpForm.markAllAsTouched();
    if (this.signUpForm.invalid) return;

    // Default: don't auto-load previously saved data unless the user explicitly
    // chooses "Use Existing Information" in the existing-customer dialog.
    try { sessionStorage.setItem('socialMediaUseExisting', 'false'); } catch { }

    this.isSignUpLoading = true;

    // OTP is bypassed in the social media flow, so we must NOT call the legacy
    // 'signup' endpoint here — that endpoint generates and EMAILS an OTP.
    // Go straight to the existing-customer check (which sends no email); it then
    // continues to proceedToRegistration() -> 'signup-and-register'.
    this.checkExistingCustomer();
  }

  checkExistingCustomer() {
    const payload = {
      email: this.signUpForm.value.email,
      firmId: this.agency.FirmId,
    };
    const url = this.apiUrl.apiUrl.socialMediaRegistration.checkExistingCustomer;
    this.apiService.doPost(url, payload).subscribe({
      next: (response) => {
        if (response.Success && response.Data?.HasExistingData) {
          this.existingCustomerData = response.Data;
          this.openExistingCustomerDialog();
        } else {
          this.proceedToRegistration();
        }
      },
      error: () => {
        this.proceedToRegistration();
      },
    });
  }

  // Show the "existing information found" popup OVER the signup form
  openExistingCustomerDialog() {
    // Stop the signup spinner while the user decides in the dialog.
    // proceedToRegistration() turns it back on when they continue.
    this.isSignUpLoading = false;

    const dialogRef = this.dialog.open(ExistingCustomerDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'existing-customer-dialog-panel',
      data: {
        customerName: this.existingCustomerData?.CustomerName || '',
      },
    });

    dialogRef.afterClosed().subscribe((result: string | null) => {
      // "use-existing" => load the customer's previously saved data on the application form.
      // "add-again" (or close) => start with a blank Customer Application.
      const useExisting = result === 'use-existing';
      try {
        sessionStorage.setItem('socialMediaUseExisting', useExisting ? 'true' : 'false');
      } catch { }
      if (!useExisting) {
        this.existingCustomerData = null;
      }
      // Both choices proceed straight to registration (no OTP).
      this.proceedToRegistration();
    });
  }

  // Bypass OTP: create the lead + user session, then go directly to the info-filling page.
  proceedToRegistration() {
    this.isSignUpLoading = true;
    const raw = this.signUpForm.getRawValue();
    const cleanMobile = (raw.mobileNumber || '').replace(/\D/g, ''); // clean 10 digits
    const payload = {
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      mobileNumber: cleanMobile,
      zipCode: raw.zipCode,
      county: raw.county,
      state: raw.state,
      firmId: this.agency.FirmId,
    };

    const url = this.apiUrl.apiUrl.socialMediaRegistration.signupAndRegister;
    this.apiService.doPost(url, payload).subscribe({
      next: (response) => {
        this.isSignUpLoading = false;
        if (response.Success && response.Data?.Token) {
          // Store session data for the customer application form
          sessionStorage.setItem('socialMediaFirmId', this.agency.FirmId.toString());
          sessionStorage.setItem('socialMediaFirmCode', this.uniqueCode);
          sessionStorage.setItem('socialMediaEmail', raw.email);
          sessionStorage.setItem('socialMediaAgency', JSON.stringify(this.agency));
          sessionStorage.setItem('socialMediaSource', 'true');

          // Auto-population data for the registration form
          sessionStorage.setItem('socialMediaFirstName', raw.firstName || '');
          sessionStorage.setItem('socialMediaLastName', raw.lastName || '');
          sessionStorage.setItem('socialMediaMobileNumber', cleanMobile || '');
          sessionStorage.setItem('socialMediaZipCode', raw.zipCode || '');
          sessionStorage.setItem('socialMediaCounty', raw.county || '');
          sessionStorage.setItem('socialMediaState', raw.state || '');

          this.storageService.setValue(StorageKey.authToken, response.Data.Token);
          this.router.navigate(['/register']);
        } else {
          this.toastr.error(response.Message || 'Registration failed.');
        }
      },
      error: () => {
        this.isSignUpLoading = false;
        this.toastr.error('Something went wrong. Please try again.');
      },
    });
  }

  // Step 2: Verify OTP
  onVerifyOtp() {
    if (this.otpForm.invalid) return;

    this.isOtpLoading = true;
    const payload = {
      email: this.signUpForm.value.email,
      otp: parseInt(this.otpForm.value.otp),
      firmId: this.agency.FirmId,
    };

    // Use the new endpoint that verifies OTP + auto-creates user + returns token
    const url = this.apiUrl.apiUrl.socialMediaRegistration.verifyOtpAndRegister;
    this.apiService.doPost(url, payload).subscribe({
      next: (response) => {
        this.isOtpLoading = false;
        if (response.Success && response.Data?.Token) {
          // Store session data for the customer application form
          sessionStorage.setItem('socialMediaFirmId', this.agency.FirmId.toString());
          sessionStorage.setItem('socialMediaFirmCode', this.uniqueCode);
          sessionStorage.setItem('socialMediaEmail', this.signUpForm.value.email);
          sessionStorage.setItem('socialMediaAgency', JSON.stringify(this.agency));
          sessionStorage.setItem('socialMediaSource', 'true');

          // Store signup form data for auto-population on the registration form
          const raw = this.signUpForm.getRawValue();
          sessionStorage.setItem('socialMediaFirstName', raw.firstName || '');
          sessionStorage.setItem('socialMediaLastName', raw.lastName || '');
          sessionStorage.setItem('socialMediaMobileNumber', (raw.mobileNumber || '').replace(/\D/g, ''));
          sessionStorage.setItem('socialMediaZipCode', raw.zipCode || '');
          sessionStorage.setItem('socialMediaCounty', raw.county || '');
          sessionStorage.setItem('socialMediaState', raw.state || '');

          // Store the auth token (needed for /register route which requires auth)
          this.storageService.setValue(StorageKey.authToken, response.Data.Token);

          // Navigate directly to the customer application form (skip signup-questions)
          this.router.navigate(['/register']);
        } else {
          this.toastr.error(response.Message || 'Invalid OTP.');
        }
      },
      error: () => {
        this.isOtpLoading = false;
        this.toastr.error('OTP verification failed.');
      },
    });
  }

  resendOtp() {
    const payload = {
      email: this.signUpForm.value.email,
      firmId: this.agency.FirmId,
    };
    const url = this.apiUrl.apiUrl.socialMediaRegistration.resendOtp;
    this.apiService.doPost(url, payload).subscribe({
      next: (response) => {
        if (response.Success) {
          this.toastr.success('OTP resent successfully.');
          this.startOtpTimer();
        } else {
          this.toastr.error(response.Message || 'Failed to resend OTP.');
        }
      },
      error: () => {
        this.toastr.error('Failed to resend OTP.');
      },
    });
  }

  startOtpTimer() {
    this.otpResendTimer = 60;
    if (this.otpInterval) clearInterval(this.otpInterval);
    this.otpInterval = setInterval(() => {
      this.otpResendTimer--;
      if (this.otpResendTimer <= 0) {
        clearInterval(this.otpInterval);
      }
    }, 1000);
  }

  // Called when customer registration form is submitted (from embedded registration component)
  onRegistrationComplete() {
    this.currentStep = 'success';
  }

  ngOnDestroy() {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
  }
}
